import { apiHelper } from "./api";
import { API_ENDPOINTS } from "./constants";
import logger from "./logger";

const GOOGLE_SCOPE = "openid email profile";
const GOOGLE_IDENTITY_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let googleIdentityScriptPromise = null;

const getGoogleClientId = () =>
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID ||
  null;

const loadGoogleIdentityScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("Google sign-in is only available in the browser."),
    );
  }

  if (window.google?.accounts?.oauth2) {
    return Promise.resolve(window.google);
  }

  if (!googleIdentityScriptPromise) {
    googleIdentityScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`,
      );

      if (existingScript && window.google?.accounts?.oauth2) {
        resolve(window.google);
        return;
      }

      const script = existingScript || document.createElement("script");
      script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
      script.async = true;
      script.defer = true;

      const handleLoad = () => {
        if (window.google?.accounts?.oauth2) {
          resolve(window.google);
        } else {
          reject(
            new Error(
              "Google identity services loaded, but the OAuth client is unavailable.",
            ),
          );
        }
      };

      const handleError = () => {
        reject(
          new Error("Failed to load Google sign-in services from Google."),
        );
      };

      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener("error", handleError, { once: true });

      if (!existingScript) {
        document.head.appendChild(script);
      }
    });
  }

  return googleIdentityScriptPromise;
};

const requestGoogleAccessToken = async (clientId) => {
  await loadGoogleIdentityScript();

  return new Promise((resolve, reject) => {
    const oauth2 = window.google?.accounts?.oauth2;

    if (!oauth2) {
      reject(
        new Error(
          "Google identity services are unavailable in this browser session.",
        ),
      );
      return;
    }

    const tokenClient = oauth2.initTokenClient({
      client_id: clientId,
      scope: GOOGLE_SCOPE,
      callback: (response) => {
        if (response?.error) {
          const tokenError = new Error(
            response.error_description ||
              response.error ||
              "Google sign-in failed.",
          );
          tokenError.code = response.error;
          reject(tokenError);
          return;
        }

        if (!response?.access_token) {
          reject(new Error("Google sign-in did not return an access token."));
          return;
        }

        resolve(response.access_token);
      },
    });

    try {
      tokenClient.requestAccessToken({ prompt: "select_account" });
    } catch (error) {
      reject(error);
    }
  });
};

const fetchGoogleProfile = async (accessToken) => {
  const response = await fetch(
    "https://www.googleapis.com/oauth2/v2/userinfo",
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Unable to load Google profile information.");
  }

  return response.json();
};

const buildRedirectMismatchError = (error) => {
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "this app";
  const clientId = getGoogleClientId() || "missing-client-id";
  const message =
    `Google sign-in is misconfigured for ${origin}. ` +
    `The Google OAuth client used by REACT_APP_GOOGLE_CLIENT_ID must authorize this origin, ` +
    `and the backend must be linked to the same Google project. ` +
    `Check the Google Cloud OAuth client settings and make sure the app is using the correct Firebase/Google project. ` +
    `(client_id: ${clientId})`;

  const redirectMismatchError = new Error(message);
  redirectMismatchError.code = "auth/google-redirect-uri-mismatch";
  redirectMismatchError.originalError = error;
  return redirectMismatchError;
};

/**
 * Handle Google Sign In / Sign Up.
 *
 * Flow:
 *  1. Authenticate with Google Identity Services using the public client ID.
 *  2. Fetch the Google profile for the selected account.
 *  3. Send the Google token and profile to the backend, which creates or
 *     updates the MongoDB user and returns the app JWT.
 */
export async function handleGoogleAuth() {
  try {
    const clientId = getGoogleClientId();
    if (!clientId) {
      throw Object.assign(
        new Error(
          "Google sign-in is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in frontend/.env and rebuild the app.",
        ),
        { code: "auth/google-client-id-missing" },
      );
    }

    const accessToken = await requestGoogleAccessToken(clientId);
    const profile = await fetchGoogleProfile(accessToken);

    const email = (profile?.email || "").trim().toLowerCase();
    if (!email) {
      throw new Error("Google sign-in did not return an email address.");
    }

    if (profile?.verified_email === false) {
      throw Object.assign(
        new Error(
          "Your Google account email has not been verified. Please verify your Google account and try again.",
        ),
        { code: "auth/google-email-not-verified" },
      );
    }

    const backendResponse = await apiHelper.post(API_ENDPOINTS.GOOGLE_AUTH, {
      idToken: accessToken,
      email,
      name: profile?.name || email.split("@")[0],
      photoURL: profile?.picture || null,
    });

    return backendResponse;
  } catch (error) {
    const errorMessage = String(error?.message || "");

    if (
      error?.code === "auth/google-client-id-missing" ||
      error?.code === "auth/google-email-not-verified"
    ) {
      logger.error(
        "Google authentication configuration error",
        error,
        "GoogleAuth",
      );
      throw error;
    }

    if (
      error?.code === "auth/redirect_uri_mismatch" ||
      errorMessage.includes("redirect_uri_mismatch") ||
      errorMessage.includes("origin_mismatch")
    ) {
      const redirectMismatchError = buildRedirectMismatchError(error);
      logger.error(
        "Google authentication redirect URI mismatch",
        {
          host:
            typeof window !== "undefined" && window.location
              ? window.location.origin
              : "unknown",
          originalMessage: error.message,
        },
        "GoogleAuth",
      );
      throw redirectMismatchError;
    }

    if (error?.code === "auth/unauthorized-domain") {
      const host =
        typeof window !== "undefined" && window.location
          ? window.location.hostname
          : "this domain";
      const unauthorizedDomainError = new Error(
        `Google sign-in is not enabled for ${host}. Add this domain in Firebase Authentication > Settings > Authorized domains, then retry.`,
      );
      unauthorizedDomainError.code = error.code;
      logger.error(
        "Google authentication unauthorized domain",
        {
          host,
          originalMessage: error.message,
        },
        "GoogleAuth",
      );
      throw unauthorizedDomainError;
    }

    logger.error("Google authentication error", error, "GoogleAuth");
    throw error;
  }
}
